import PortableText from '@otovo/shared/components/Sanity/PortableTextComponents';
import ArrowRightSvg from '@otovo/shared/images/icons/arrow-right.svg';
import CircleCheck from '@otovo/shared/images/icons/circle-check.svg';
import { Cloud$MainHardwareType } from '@otovo/shared/types/cloudEnums';
import { Sanity$HeroSection } from '@otovo/shared/types/sanityTypes';
import cx from 'classnames';
import { stegaClean } from 'next-sanity';
import Badge from '../Badge/Badge';
import CTAButton from '../CTAButton';
import HeroAddressSearchBar from './HeroAddressSearchBar';
import TrustVendor from './TrustVendor';

type Props = {
  heroSection: Sanity$HeroSection;
  mainHardwareType?: Cloud$MainHardwareType;
};

const contentWrapperClasses = 'max-w-8xl sm:px-0 2xl:mx-auto 2xl:px-16 px-4';

const CampaignHeroSection = ({
  heroSection,
  mainHardwareType = 'pv_system',
}: Props) => {
  if (!heroSection.campaignFields) {
    return null;
  }
  const { headline, lead, badge } = heroSection.campaignFields;
  const hasAddressBar = stegaClean(heroSection.addressBarToggle);
  const hasCTA = !hasAddressBar && stegaClean(heroSection.ctaButton);

  return (
    <div
      className={cx(
        'isolate w-full pt-26 pb-6 sm:h-[32rem] sm:py-0 xl:h-[36rem]',
        'bg-[linear-gradient(180deg,rgba(27,36,56,0.01)_0%,rgba(27,36,56,0.3)_5%,rgba(27,36,56,0.6)_10%,rgba(27,36,56,0.87)_20%,rgba(27,36,56,0.92)_50.96%,rgba(27,36,56,1)_65.71%)] sm:bg-none',
        contentWrapperClasses,
      )}
    >
      <div
        className={cx(
          'grid h-full w-full text-heading',
          'sm:grid-cols-12',
          'md:mt-12 lg:mt-24',
          'pb-[10vh]',
          'z-20',
        )}
        id="nav-scroll-trigger"
      >
        <div className="col-start-1 sm:col-span-6 sm:!col-start-2 md:col-span-5 2xl:!col-start-1">
          {badge && <Badge badge={badge} />}
          <PortableText
            value={headline}
            components={{
              block: {
                normal: ({ children }) => (
                  <h1 className="text-3xl font-semibold text-balance text-shadow-2xs text-shadow-black md:text-4xl lg:text-5xl">
                    {children}
                  </h1>
                ),
              },
              marks: {
                highlight: ({
                  children: content,
                }: {
                  children: React.ReactNode;
                }) => <em className="text-peach-50 not-italic">{content}</em>,
              },
            }}
          />
          <div className="mt-1 overflow-hidden">
            {lead && (
              <PortableText
                value={lead}
                components={{
                  block: {
                    normal: ({ children }) => (
                      <span className="text-base font-normal text-body text-shadow-2xs text-shadow-black md:text-lg xl:text-xl">
                        {children}
                      </span>
                    ),
                  },
                  listItem: {
                    bullet: ({ children }) => (
                      <li className="flex gap-3 text-balance">
                        <CircleCheck className="mt-[0.25em] size-4 shrink-0 fill-green-20 text-grey-90 md:mt-[2px] md:size-5" />
                        <span className="font-normal text-heading sm:font-medium">
                          {children}
                        </span>
                      </li>
                    ),
                  },
                  marks: {
                    strongAccent: ({
                      children: content,
                    }: {
                      children: React.ReactNode;
                    }) => (
                      <em className="text-3xl font-semibold text-peach-50">
                        {content}
                      </em>
                    ),
                  },
                }}
              />
            )}
          </div>
          <div className="mt-6 text-muted sm:mt-10">
            {hasAddressBar && (
              <HeroAddressSearchBar
                mainHardwareType={mainHardwareType}
                description={heroSection.addressBarDescription}
              />
            )}
            {hasCTA && (
              <CTAButton href={heroSection.ctaButton.href}>
                {heroSection.ctaButton.label}{' '}
                <span
                  className="ml-1 inline-block animate-bounce"
                  style={{ animationIterationCount: 1.5, animationDelay: '3s' }}
                >
                  <ArrowRightSvg className="size-3 rotate-90" />
                </span>
              </CTAButton>
            )}
          </div>
        </div>
      </div>
      {/* Trust Vendor is displayed at the bottom, underneath the rest */}
      {heroSection.trustVendor && (
        <div className="absolute right-0 -bottom-14 left-0 w-full bg-blue-100 2xl:bottom-0">
          <div className={cx('grid sm:grid-cols-12', contentWrapperClasses)}>
            <aside className="py-4 sm:col-start-2 2xl:col-start-1">
              {heroSection.trustVendor && (
                <TrustVendor {...heroSection.trustVendor} />
              )}
            </aside>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignHeroSection;
